import { computed, ref } from 'vue';
import SalesPropertyAddressSection from '../components/SalesPropertyAddressSection.vue';
import SalesPropertyClausesSection from '../components/SalesPropertyClausesSection.vue';
import SalesPropertyDeadlineSection from '../components/SalesPropertyDeadlineSection.vue';
import SalesPropertyDocumentsSection from '../components/SalesPropertyDocumentsSection.vue';
import SalesPropertyIdentitySection from '../components/SalesPropertyIdentitySection.vue';
import SalesPropertyLaudemioSection from '../components/SalesPropertyLaudemioSection.vue';
import SalesPropertyRegistrySection from '../components/SalesPropertyRegistrySection.vue';
import SalesPropertyValuesSection from '../components/SalesPropertyValuesSection.vue';
import { propertySections } from './salesPropertyFormOptions';
import {
  salesDeliveryLocation,
  salesReviewLocation,
  shouldReturnToSalesDelivery,
} from './salesReviewNavigation';
import { saveReviewNavigationBeforeSwitch } from './salesReviewNavigationPersistence';

const sectionComponents = {
  identity: SalesPropertyIdentitySection,
  address: SalesPropertyAddressSection,
  registry: SalesPropertyRegistrySection,
  laudemio: SalesPropertyLaudemioSection,
  values: SalesPropertyValuesSection,
  deadline: SalesPropertyDeadlineSection,
  clauses: SalesPropertyClausesSection,
  documents: SalesPropertyDocumentsSection,
};

export const useSalesPropertyRegistrationPage = ({
  form,
  id,
  certificateChecks,
  propertyCatalog,
  registration,
  reviewCompletion,
  reviewContext,
  reviewMode,
  route,
  router,
  t,
}) => {
  const correctionsOpen = ref(false);
  const currentSection = computed(
    () => sectionComponents[form.activeSection.value]
  );
  const currentSectionBindings = computed(() => {
    if (form.activeSection.value === 'documents') {
      return {
        canEdit:
          registration.transaction.value?.permissions
            ?.can_edit_property_documents,
        transactionId: id.value,
      };
    }
    if (form.activeSection.value === 'registry') {
      return {
        catalog: propertyCatalog.catalog.value,
        certificateChecks: certificateChecks.checks.value,
        certificateChecksVisible: certificateChecks.visible.value,
        checkingCertificates: certificateChecks.checking.value,
      };
    }
    if (form.activeSection.value === 'laudemio') {
      return { catalog: propertyCatalog.catalog.value };
    }
    return {};
  });
  const currentSectionListeners = computed(() => {
    if (form.activeSection.value === 'documents')
      return { changed: form.updateDocuments };
    if (form.activeSection.value !== 'registry') return {};

    return {
      checkCertificates: async () => {
        const result = await certificateChecks.run({
          inscricao: form.profile.value.inscricaoMunicipal,
          municipio: form.profile.value.cidade,
        });
        const hasFunesbomDebt = result?.checks?.some(
          check => check.source === 'funesbom' && Boolean(check.debitos)
        );
        if (hasFunesbomDebt)
          form.updateNested('pendenciasImovel', 'taxaIncendioAtrasada', true);
      },
      closeCertificateChecks: certificateChecks.close,
    };
  });
  const translatedSections = computed(() => propertySections(t));
  const currentTitle = computed(
    () =>
      translatedSections.value.find(
        section => section.key === form.activeSection.value
      )?.label || t('SALES_PROPERTY.TITLE')
  );
  const progressLabel = computed(() =>
    t('SALES.COMMON.PERCENT', {
      value: registration.transaction.value?.progress?.percent || 0,
    })
  );
  const completeReviewSection = section => {
    if (!reviewMode.value || !reviewCompletion?.save) return true;

    return reviewCompletion.save({
      section: 'imovel',
      checks: reviewContext.value?.sectionChecks?.[section] || [],
    });
  };

  const goBack = () => {
    if (route.query?.return_to === 'detail') {
      return router.push({
        name: 'operacao_imobiliaria_vendas_detalhes',
        params: {
          accountId: route.params.accountId,
          salesTransactionId: id.value,
        },
        query: { detail_tab: 'property' },
      });
    }
    if (reviewMode.value) {
      return router.push(
        salesReviewLocation({
          accountId: route.params.accountId,
          salesTransactionId: id.value,
        })
      );
    }
    if (shouldReturnToSalesDelivery(route)) {
      return router.push(
        salesDeliveryLocation({
          accountId: route.params.accountId,
          salesTransactionId: id.value,
        })
      );
    }
    return router.push({
      name: 'operacao_imobiliaria_vendas_cadastro',
      params: {
        accountId: route.params.accountId,
        salesTransactionId: id.value,
      },
    });
  };
  const saveAndExit = async () => {
    const section = form.activeSection.value;
    const saved = await form.save();
    if (!saved) return false;
    const completed = await completeReviewSection(section);
    if (completed) goBack();
    return completed;
  };
  const next = async () => {
    const section = form.activeSection.value;
    const advanced = await form.next();
    if (!advanced || !reviewMode.value) return advanced;
    if (await completeReviewSection(section)) return true;

    form.goTo(section);
    return false;
  };
  const selectSection = targetSection =>
    saveReviewNavigationBeforeSwitch({
      completeCurrent: completeReviewSection,
      currentSection: form.activeSection.value,
      reviewMode: reviewMode.value,
      saveCurrent: form.save,
      switchTo: form.goTo,
      targetSection,
    });

  return {
    closeCorrections: () => {
      correctionsOpen.value = false;
    },
    correctionsOpen,
    currentSection,
    currentSectionBindings,
    currentSectionListeners,
    currentTitle,
    goBack,
    next,
    openCorrections: () => {
      correctionsOpen.value = true;
    },
    previous: form.previous,
    progressLabel,
    save: saveAndExit,
    saveAndExit,
    selectSection,
  };
};
